import { CopyError } from '../copy/text.js';
import { viewerStrings } from '../copy/strings.js';
import { baseQuery } from '@cdr-clarity/query-service';
import {
  queryLimits,
  type DatasetDescriptor,
  type QueryDefinition,
  type QueryExpression,
  type QueryScope,
} from '@cdr-clarity/contracts';
import { FieldState } from './field-state.js';
import { predicateFields } from './query-references.js';
import { recordPage, type ExecuteQuery, type RecordPage } from './record-page.js';

export interface RecordsSnapshot {
  readonly scope: QueryScope;
  readonly selected: ReadonlySet<string>;
  readonly hidden: readonly string[];
  readonly focus: readonly string[] | null;
  readonly generation: number;
  readonly invalidFields: readonly string[];
}

/** Presentation state only; full-dataset ordering/filtering runs through ExecuteQuery. */
export class RecordsState {
  private snapshot: RecordsSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly stopFields: () => void;
  private queryIdentity = '';
  private fieldIdentity = '';
  private cursors = new Map<number, readonly string[] | null>([[0, null]]);
  constructor(
    readonly fields: FieldState,
    descriptor: DatasetDescriptor,
    private readonly execute: ExecuteQuery,
  ) {
    this.snapshot = {
      scope: {
        datasetId: descriptor.id,
        revision: descriptor.revision,
        classificationRevision: descriptor.classificationRevision,
        sources: [],
        collection: descriptor.effectiveCdrRecords ? 'cdr' : 'cmr',
      },
      selected: new Set(),
      hidden: [],
      focus: null,
      generation: 0,
      invalidFields: [],
    };
    this.stopFields = fields.subscribe(() => {
      const { preference } = fields.getSnapshot();
      const identity = JSON.stringify([
        preference.visible,
        preference.sort,
        preference.predicate,
        preference.pageSize,
      ]);
      if (identity !== this.fieldIdentity) {
        this.fieldIdentity = identity;
        this.invalidate();
      }
    });
    const { preference } = fields.getSnapshot();
    this.fieldIdentity = JSON.stringify([
      preference.visible,
      preference.sort,
      preference.predicate,
      preference.pageSize,
    ]);
    this.invalidate();
  }
  getSnapshot = (): RecordsSnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  private publish(patch: Partial<RecordsSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }
  private invalidate(): void {
    const { preference, visible } = this.fields.getSnapshot();
    const visibleIds = new Set(visible.map((field) => field.id));
    const referenced = [
      ...(!this.snapshot.focus && preference.predicate
        ? predicateFields(preference.predicate)
        : []),
      ...preference.sort.map((order) => order.key),
    ];
    this.cursors = new Map([[0, null]]);
    this.publish({
      generation: this.snapshot.generation + 1,
      invalidFields: [...new Set(referenced.filter((id) => !visibleIds.has(id)))],
    });
  }
  definition(): QueryDefinition {
    if (this.snapshot.invalidFields.length)
      throw new CopyError(viewerStrings.webStateRecordsState.thisQueryUsesHiddenFieldsShowThose);
    return { ...this.reportDefinition(), order: this.fields.getSnapshot().preference.sort };
  }
  /** Reports retain data scope and filters, independent of table columns and sorting. */
  reportDefinition(): QueryDefinition {
    const { preference } = this.fields.getSnapshot();
    return {
      ...baseQuery({
        ...this.snapshot.scope,
        ...(this.snapshot.focus ? { recordIds: [...this.snapshot.focus] } : {}),
        excludedRecordIds: this.snapshot.focus ? [] : [...this.snapshot.hidden],
      }),
      order: [],
      page: { size: preference.pageSize },
      ...(!this.snapshot.focus && preference.predicate ? { predicate: preference.predicate } : {}),
    };
  }
  async page(offset: number, size: number, order: QueryDefinition['order']): Promise<RecordPage> {
    if (
      !Number.isSafeInteger(size) ||
      size < 1 ||
      size > queryLimits.responseRows ||
      !Number.isSafeInteger(offset) ||
      offset < 0 ||
      offset % size !== 0
    )
      throw new CopyError(viewerStrings.webStateRecordsState.requestedRowBlockDoesNotAlignWith);
    const definition = { ...this.definition(), order, page: { size } };
    const columns = this.fields
      .getSnapshot()
      .visible.filter((field) => field.collections.includes(definition.scope.collection))
      .map((field) => field.id);
    if (order.some((item) => !columns.includes(item.key)))
      throw new CopyError(viewerStrings.webStateRecordsState.sortingUsesHiddenOrUnavailableFields);
    const key = JSON.stringify({ definition, columns });
    if (key !== this.queryIdentity) {
      this.queryIdentity = key;
      this.cursors = new Map([[0, null]]);
    }
    const generation = this.snapshot.generation;
    let start = Math.max(...[...this.cursors.keys()].filter((index) => index <= offset));
    while (true) {
      const page = await recordPage(this.execute, definition, columns, this.cursors.get(start)!);
      if (generation !== this.snapshot.generation || key !== this.queryIdentity)
        throw new CopyError(
          viewerStrings.webStateRecordsState.theRecordQueryChangedWhileLoadingThis,
        );
      if (page.nextCursors) this.cursors.set(start + size, page.nextCursors);
      if (this.cursors.size > 200) {
        const oldest = [...this.cursors.keys()].find(
          (index) => index !== 0 && index !== start + size,
        )!;
        this.cursors.delete(oldest);
      }
      if (start === offset) return page;
      if (!page.nextCursors)
        throw new CopyError(viewerStrings.webStateRecordsState.theRequestedPageIsOutsideThisResult);
      start += size;
    }
  }
  setFilter(predicate: QueryExpression | undefined): void {
    this.publish({ focus: null });
    this.fields.update({ predicate });
  }
  select(ids: Iterable<string>): void {
    this.publish({ selected: new Set(ids) });
  }
  hideSelected(): void {
    this.publish({
      hidden: [...new Set([...this.snapshot.hidden, ...this.snapshot.selected])],
      selected: new Set(),
      focus: null,
    });
    this.invalidate();
  }
  showHidden(): void {
    this.publish({ hidden: [] });
    this.invalidate();
  }
  focus(ids: readonly string[]): void {
    this.publish({ focus: [...ids], selected: new Set() });
    this.invalidate();
  }
  returnToResults(): void {
    this.publish({ focus: null, selected: new Set() });
    this.invalidate();
  }
  adoptRevision(descriptor: DatasetDescriptor): void {
    this.publish({
      scope: {
        ...this.snapshot.scope,
        revision: descriptor.revision,
        classificationRevision: descriptor.classificationRevision,
      },
      selected: new Set(),
      focus: null,
    });
    this.invalidate();
  }
  dispose(): void {
    this.stopFields();
    this.listeners.clear();
  }
}
